import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Share2 } from "lucide-react";
import { api, API } from "@/lib/api";
import ProfessionalMapContactDialog from "@/components/professional-map/ProfessionalMapContactDialog";
import ProfessionalMapReviews from "@/components/professional-map/ProfessionalMapReviews";
import ProfessionalProfileView from "@/components/professional-map/ProfessionalProfileView";

export default function ProfessionalMapPublicProfile() {
  const { cardId } = useParams();
  const [state, setState] = useState({ loading: true, profile: null, error: false });
  const [shareStatus, setShareStatus] = useState("");
  const [openedPhoto, setOpenedPhoto] = useState(null);
  const [contactMode, setContactMode] = useState(null);
  useEffect(() => {
    let live = true;
    setState({ loading: true, profile: null, error: false });
    api.get("/professional-map/profiles/" + encodeURIComponent(cardId || ""))
      .then(({ data }) => { if (live) setState({ loading: false, profile: data, error: false }); })
      .catch(() => { if (live) setState({ loading: false, profile: null, error: true }); });
    return () => { live = false; };
  }, [cardId]);

  const item = state.profile;
  const workIds = item?.portfolio_media_ids || [];
  const selectedIndex = openedPhoto ? workIds.indexOf(openedPhoto) : -1;
  useEffect(() => {
    setOpenedPhoto(null);
  }, [cardId]);
  useEffect(() => {
    if (!openedPhoto) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpenedPhoto(null);
      if (event.key === "ArrowRight") setOpenedPhoto((id) => {
        const index = workIds.indexOf(id);
        return workIds[Math.min(index + 1, workIds.length - 1)] || id;
      });
      if (event.key === "ArrowLeft") setOpenedPhoto((id) => {
        const index = workIds.indexOf(id);
        return workIds[Math.max(0, index - 1)] || id;
      });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openedPhoto, workIds.length]);
  async function shareProfile() {
    // Only an already fetched, safely published public profile is shareable.
    if (!item || !cardId) return;
    const url = window.location.origin + "/professional-map/profiles/" + encodeURIComponent(cardId);
    try {
      if (navigator.share) {
        await navigator.share({ title: item.public_name, url });
        return;
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setShareStatus("Ссылка скопирована");
      } else {
        setShareStatus("Скопируй ссылку из адресной строки");
      }
    } catch (error) {
      if (error?.name !== "AbortError") setShareStatus("Скопируй ссылку из адресной строки");
    }
  }
  return (
    <div className="min-h-screen bg-[#F7F0E5] text-[#3A2B22]" data-testid="pm-public-profile">
      <main className="mx-auto max-w-[900px] px-5 pb-20 pt-7 sm:px-9">
        <Link to="/professional-map" className="inline-flex min-h-[44px] items-center gap-2 text-sm text-[#705C4D]">
          <ArrowLeft size={17} aria-hidden="true"/> К Профессиональной карте
        </Link>
        {state.loading ? <p role="status" className="mt-7">Загружаем страницу…</p>
        : state.error || !item ? <p role="status" className="mt-7 rounded-2xl border border-[#826751]/20 bg-white/75 p-6">
          Страница не найдена, скрыта или сейчас недоступна.
        </p> : (
          <ProfessionalProfileView
            key={cardId} profile={item}
            shareAction={<div className="flex flex-col gap-1">
              <button type="button" onClick={shareProfile}
                className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-[#826751]/25 px-4 text-sm hover:bg-[#F9F2E9]"
                data-testid="pm-public-profile-share" aria-label="Поделиться профессиональной страницей">
                <Share2 size={16} aria-hidden="true"/> Поделиться
              </button>
              {shareStatus && <p className="text-xs text-[#705C4D]" role="status">{shareStatus}</p>}
            </div>}
            contactActions={<div className="grid gap-2" data-testid="pm-contact-actions">
              <button type="button" onClick={() => setContactMode("contact")} data-testid="pm-open-contact"
                className="min-h-11 rounded-xl border border-[#826751]/25 px-4 text-sm">Связаться</button>
              <button type="button" onClick={() => setContactMode("quote")} data-testid="pm-open-quote"
                className="min-h-11 rounded-xl border border-[#826751]/25 bg-[#F4EADD] px-4 text-sm font-medium text-[#4A382B] transition-colors hover:bg-[#EADCC9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#755F4E]">Запросить предложение</button>
            </div>}
            renderPortrait={(id, profile) => <img
              src={API + "/professional-map/profiles/" + encodeURIComponent(cardId) + "/media/" + encodeURIComponent(id)}
              alt={"Портрет: " + profile.public_name} className="aspect-[5/6] w-full object-cover"
              style={{objectPosition: (profile.portrait_crop_x ?? 50) + "% " + (profile.portrait_crop_y ?? 50) + "%"}}/>}
            renderWork={(id, index, caption) => (
              <button type="button" className="block w-full overflow-hidden rounded-xl focus-visible:outline focus-visible:outline-2"
                onClick={() => setOpenedPhoto(id)} aria-label={"Посмотреть работу " + (index + 1)}>
                <img loading="lazy" className="aspect-[4/5] w-full object-cover"
                  src={API + "/professional-map/profiles/" + encodeURIComponent(cardId) + "/media/" + encodeURIComponent(id)}
                  alt={caption || "Работа " + (index + 1)}/>
              </button>
            )}
            reviews={<ProfessionalMapReviews cardId={cardId}/>}
          />
        )}
        {item && contactMode && <ProfessionalMapContactDialog cardId={cardId} profile={item} mode={contactMode} onClose={() => setContactMode(null)} />}
        {openedPhoto && item?.portfolio_media_ids?.includes(openedPhoto) && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" role="dialog" aria-modal="true" aria-label="Просмотр работы" onClick={() => setOpenedPhoto(null)}>
          <button type="button" onClick={() => setOpenedPhoto(null)} className="absolute right-5 top-5 rounded-lg bg-white px-4 py-2 text-sm text-[#3A2B22]">Закрыть</button>
          <button type="button" disabled={selectedIndex <= 0}
            onClick={(event) => { event.stopPropagation(); setOpenedPhoto(workIds[selectedIndex - 1]); }}
            aria-label="Предыдущая фотография"
            className="mr-2 rounded-full bg-white p-3 text-[#3A2B22] disabled:opacity-30"><ChevronLeft size={18}/></button>
          <figure className="max-w-[78vw]" onClick={(event) => event.stopPropagation()}>
            <img className="max-h-[78vh] max-w-full rounded-lg object-contain"
              src={API + "/professional-map/profiles/" + encodeURIComponent(cardId) + "/media/" + encodeURIComponent(openedPhoto)}
              alt={item.portfolio_captions?.[openedPhoto] || "Работа"} />
            {item.portfolio_captions?.[openedPhoto] && <figcaption className="mt-2 text-center text-sm text-white">
              {item.portfolio_captions[openedPhoto]}</figcaption>}
          </figure>
          <button type="button" disabled={selectedIndex >= workIds.length - 1}
            onClick={(event) => { event.stopPropagation(); setOpenedPhoto(workIds[selectedIndex + 1]); }}
            aria-label="Следующая фотография"
            className="ml-2 rounded-full bg-white p-3 text-[#3A2B22] disabled:opacity-30"><ChevronRight size={18}/></button>
        </div>}
      </main>
    </div>
  );
}
